import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  PackageCheck,
  Clock,
  Eye,
  CheckCircle,
  Truck,
  XCircle,
  AlertTriangle,
  Trash,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AlertModal from "../../components/AlertModal";
import ConfirmModal from "../../components/ConfirmModal";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [visibleCount, setVisibleCount] = useState(5);
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });

  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // 🔹 Status Map
  const statusMap = {
    paid: {
      label: "Confirmed",
      color: "bg-blue-100 text-blue-800",
      icon: <CheckCircle size={16} />,
    },
    processing: {
      label: "Confirmed",
      color: "bg-blue-100 text-blue-800",
      icon: <CheckCircle size={16} />,
    },
    "in transit": {
      label: "In Transit",
      color: "bg-yellow-100 text-yellow-800",
      icon: <Truck size={16} />,
    },
    delivered: {
      label: "Delivered",
      color: "bg-green-100 text-green-800",
      icon: <PackageCheck size={16} />,
    },
    cancelled: {
      label: "Cancelled",
      color: "bg-red-100 text-red-800",
      icon: <XCircle size={16} />,
    },
    "failed delivery": {
      label: "Failed Delivery",
      color: "bg-orange-100 text-orange-800",
      icon: <AlertTriangle size={16} />,
    },
  };

  useEffect(() => {
    if (loading) return;

    const fetchOrders = async () => {
      try {
        if (!user) {
          console.warn("No user in memory, trying session cookie.");
          return;
        }

        const { data } = await axios.get(`${BASE_URL}/api/orders/my`, {
          withCredentials: true,
        });

        const sorted = [...data].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );

        setOrders(sorted);
      } catch (err) {
        console.error("Failed to load orders", err);
        if (err.response?.status === 401) {
          logout();
          navigate("/signin");
        }
      }
    };

    fetchOrders();
  }, [BASE_URL, logout, navigate, user, loading]);

  // 🔹 Filtered Orders
  const filteredOrders = orders.filter((o) => {
    const status = o.status?.toLowerCase();

    if (statusFilter === "All") return true;

    if (statusFilter === "Confirmed") {
      return status === "paid" || status === "processing";
    }

    if (statusFilter === "Cancelled/Failed") {
      return status === "cancelled" || status === "failed delivery";
    }

    return statusMap[status]?.label === statusFilter;
  });

  // 🔹 Item Summary (unchanged)
    const renderItemSummary = (grouped, hamperType) => {
    // Build hamper items summary
    const hamperItemsSummary = (grouped?.hamperItems || [])
      .map((item) => `${item.name} ×${item.quantity || 1}`)
      .join(", ");

    // Build extras summary (flowers, accessories, VIPs)
    const extras = [
      ...(grouped?.extraFlowers || []),
      ...(grouped?.extraAccessories || []),
      ...(grouped?.extraVIPs || []),
    ];

    const extrasSummary = extras
      .map((item) => `${item.name} ×${item.quantity || 1}`)
      .join(", ");

    // Final formatted summary
    if (hamperItemsSummary && extrasSummary) {
      return (
        <span>
          <strong>{hamperType}</strong> ({hamperItemsSummary}), {extrasSummary}
        </span>
      );
    } else if (hamperItemsSummary) {
      return (
        <span>
          <strong>{hamperType}</strong> ({hamperItemsSummary})
        </span>
      );
    } else if (extrasSummary) {
      return <span>{extrasSummary}</span>;
    } else {
      return "No items available";
    }
  };

  const handleViewReceipt = (order) => {
    if (!order?._id) {
      setAlert({
        open: true,
        message: "Order ID missing.",
        type: "error",
      });
      return;
    }
    navigate(`/order-confirmation/${order._id}`);
  };

  const handleCancelOrder = async (orderId) => {
    try {
      // Optimistic update: update the UI immediately
      setOrders((prev) =>
        prev.map((order) =>
          order._id === orderId ? { ...order, status: "Cancelled" } : order
        )
      );

      const res = await fetch(`${BASE_URL}/api/orders/${orderId}/cancel`, {
        method: "PUT",
        credentials: "include",
      });

      if (!res.ok) {
        const errData = await res.json();
        console.error("Cancel failed:", errData);

        // Revert optimistic update if failed
        setOrders((prev) =>
          prev.map((order) =>
            order._id === orderId ? { ...order, status: order.status } : order
          )
        );

        alert(errData.message || "Failed to cancel order");
      }
    } catch (err) {
      console.error("Cancel order error:", err);
      setAlert({
        open: true,
        message: "Something went wrong, please try again.",
        type: "error",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mb-4">My Orders</h2>
        
        <button
          onClick={() => navigate("/track-order")}
          className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition"
        >
          Track My Order
        </button>
      </div>

      {/* 🔹 Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 justify-center sm:justify-start">
        {["All", "Confirmed", "In Transit", "Delivered", "Cancelled/Failed"].map(
          (status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors
                ${
                  statusFilter === status
                    ? "bg-blue-600 text-white border-blue-600"
                    : "text-gray-700 border-gray-200 hover:bg-gray-200"
                }`}
            >
              {status}
            </button>
          )
        )}
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <p className="text-gray-500">No orders found.</p>
      ) : (
        <div className="space-y-4">
          {filteredOrders.slice(0, visibleCount).map((order) => {
            const statusInfo =
              statusMap[order.status?.toLowerCase()] || {
                label: "Unknown",
                color: "bg-gray-100 text-gray-600",
                icon: <Clock size={16} />,
              };

            return (
              <div
                key={order._id}
                className="border rounded-lg p-4 bg-white shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6"
              >
                {/* Left Section: Order Info + Items + Total */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 w-full flex-1">
                  {/* Order ID & Date */}
                  <div className="sm:min-w-[150px]">
                    <p className="font-semibold text-sm text-gray-800">
                      #{order.orderNumber || order._id}
                    </p>
                    <p className="text-sm text-gray-500">
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString()
                        : "Unknown Date"}
                    </p>
                  </div>

                  {/* Item Summary - 🔹 Give this the most space */}
                  <p className="text-sm text-gray-600">
                    {order.orderDetails?.grouped
                      ? renderItemSummary(order.orderDetails.grouped, order.hamperType)
                      : "No items available"}
                  </p>

                  {/* Total */}
                  <div className="text-sm text-gray-700 sm:min-w-[110px]">
                    Total:{" "}
                    <strong>
                      {order.total || order.orderDetails?.totalAmount
                        ? `$${Number(
                            order.total || order.orderDetails?.totalAmount
                          ).toFixed(2)}`
                        : "N/A"}
                    </strong>
                  </div>
                </div>

                {/* Right Section: Status + Receipt + Cancel */}
                <div className="flex w-full sm:w-auto items-center justify-between sm:justify-start sm:gap-4 sm:min-w-[240px]">
                  {/* Cancel Button - only if allowed */}
                  {(() => {
                    const now = new Date();
                    const createdAt = new Date(order.createdAt);
                    const diffInMinutes = (now - createdAt) / (1000 * 60);
                    const cancellable =
                      order.status !== "Cancelled" &&
                      order.status !== "Delivered" &&
                      diffInMinutes <= 30;

                    if (cancellable) {
                      return (
                        <button
                          onClick={() => {
                            setOrderToCancel(order._id);
                            setConfirmOpen(true);
                          }}
                          className="text-red-600 hover:underline text-sm flex items-center gap-2"
                        >
                        <Trash size={16} /> Cancel Order
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {/* View Receipt Button */}
                  <button
                    onClick={() => handleViewReceipt(order)}
                    className="text-blue-600 hover:underline text-sm flex items-center gap-2"
                  >
                    <Eye size={16} /> View Receipt
                  </button>

                  {/* 🔹 Status Badge */}
                  <span
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}
                  >
                    {statusInfo.icon} {statusInfo.label}
                  </span>
                </div>
              </div>
            );
          })}

          {visibleCount < filteredOrders.length && (
            <div className="text-center mt-4">
              <button
                onClick={() => setVisibleCount((prev) => prev + 5)}
                className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
              >
                Load More Orders
              </button>
            </div>
          )}
        </div>
      )}

      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: "", type: "info" })}
        message={alert.message}
        type={alert.type}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Cancel Order"
        message="Are you sure you want to cancel this order? This action cannot be undone."
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          if (orderToCancel) {
            await handleCancelOrder(orderToCancel); // call your cancel function
            setConfirmOpen(false);
            setOrderToCancel(null);
          }
        }}
        confirmLabel="Yes, Cancel Order"
        cancelLabel="Keep Order"
      />

    </div>
  );
};

export default Orders;
